import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Plus, Users } from "lucide-react";
import type { CrewSummary } from "@crewledger/shared/schemas";
import { EmptyState, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchCrewOptions, fetchCrews } from "./api";
import { useCrewPermissions } from "./use-crew-permissions";

export function CrewsListPage() {
  const { canWrite } = useCrewPermissions();
  const [crews, setCrews] = useState<CrewSummary[]>([]);
  const [sites, setSites] = useState<{ id: string; name: string; code: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [siteId, setSiteId] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    fetchCrewOptions()
      .then((res) => { setSites(res.data.sites); })
      .catch(() => { setSites([]); });
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchCrews({ pageSize: 100, q: q || undefined, siteId: siteId || undefined, active: activeOnly ? true : undefined })
      .then((res) => { setCrews(res.data); })
      .finally(() => { setLoading(false); });
  }, [q, siteId, activeOnly]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Crew Templates"
        description="Reusable crews with default job codes, cost codes, and equipment for daily entry."
        actions={
          canWrite ? (
            <Link to="/crews/new">
              <Button>
                <Plus className="mr-1.5 h-4 w-4" />
                New crew
              </Button>
            </Link>
          ) : undefined
        }
      />

      <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-3">
        <Input placeholder="Search crews…" value={q} onChange={(e) => { setQ(e.target.value); }} />
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); }}
          className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
        >
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code} — {s.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={activeOnly} onChange={(e) => { setActiveOnly(e.target.checked); }} />
          Active only
        </label>
      </div>

      {loading ? (
        <div className="h-64 animate-pulse rounded-xl border bg-card-white" />
      ) : crews.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No crew templates"
          description="Create a crew to speed up daily entry with default codes and equipment."
          actionLabel={canWrite ? "Create crew" : undefined}
          onAction={canWrite ? () => (window.location.href = "/crews/new") : undefined}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-bg-light text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3">Crew</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Members</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {crews.map((crew) => (
                <tr key={crew.id} className="border-b last:border-0 hover:bg-bg-light/60">
                  <td className="px-4 py-3">
                    <Link to={`/crews/${crew.id}`} className="font-semibold text-navy-deep hover:underline">
                      {crew.name}
                    </Link>
                    {crew.description ? (
                      <p className="text-xs text-muted-foreground">{crew.description}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {crew.siteName ? `${crew.siteCode} — ${crew.siteName}` : "All sites"}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    <div className="flex items-center gap-2">
                      {crew.memberCount}
                      {crew.inactiveMemberCount > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          {crew.inactiveMemberCount} inactive
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        crew.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {crew.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
